import { useMemo } from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useEventStateImportHistory } from '~/app/hooks/knowledgeBase/useEventStateImportHistory';
import {
  IMPORT_REQUEST_STATUS,
  type EventStateImportRequestItem,
} from '~/app/services/knowledgeBase/eventStateImportHistory.types';
import { formatDateTime } from '~/app/utils/format-date-time';
import { EventStateImportHistorySectionView } from './EventStateImportHistorySectionView';

const STATUS_COLORS: Record<string, string> = {
  [IMPORT_REQUEST_STATUS.IN_PROGRESS]: 'processing',
  [IMPORT_REQUEST_STATUS.SUCCEEDED]: 'success',
  [IMPORT_REQUEST_STATUS.FAILED]: 'error',
};

export const EventStateImportHistorySection = () => {
  const { t } = useTranslation('componentSchema');
  const {
    data,
    meta,
    isLoading,
    isFetching,
    error,
    refetch,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = useEventStateImportHistory();

  const hasInProgress = data.some(
    (item) => item.importStatus === IMPORT_REQUEST_STATUS.IN_PROGRESS,
  );

  const columns = useMemo<ColumnsType<EventStateImportRequestItem>>(
    () => [
      {
        title: t('importHistory.columns.filePath'),
        dataIndex: 'filePath',
        key: 'filePath',
        ellipsis: true,
        render: (value: string) => value || '-',
      },
      {
        title: t('importHistory.columns.importStatus'),
        dataIndex: 'importStatus',
        key: 'importStatus',
        render: (value: EventStateImportRequestItem['importStatus']) => (
          <Tag color={STATUS_COLORS[value] ?? 'default'}>
            {t(`importHistory.status.${value}`)}
          </Tag>
        ),
      },
      {
        title: t('importHistory.columns.importedAt'),
        dataIndex: 'importedAt',
        key: 'importedAt',
        render: (value: string | null) => formatDateTime(value),
      },
      {
        title: t('importHistory.columns.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: t('importHistory.columns.errorReport'),
        dataIndex: 'errorReport',
        key: 'errorReport',
        ellipsis: true,
        render: (value: string | null) => value || '-',
      },
    ],
    [t],
  );

  return (
    <EventStateImportHistorySectionView
      columns={columns}
      data={data}
      error={error}
      hasInProgress={hasInProgress}
      isFetching={isFetching}
      isLoading={isLoading}
      onPageChange={(nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
      }}
      onRefresh={() => {
        void refetch();
      }}
      page={page}
      pageSize={pageSize}
      total={meta?.total}
    />
  );
};
